import Batcher from './Batcher';
import { Batch } from './Batch';
import { modelAttribute } from './modelBuilder';
import type ApplicationModel from './ApplicationModel';
import { type QueryModel } from './Query';
import type Query from './Query';

export class QueryBatcher extends Batcher {
  createBatch() {
    return new Batch();
  }

  /**
   * Find a model by id
   */
  async find<M extends typeof ApplicationModel>(
    query: Query<M, any>,
    id: string
  ): Promise<M> {
    const firstQuery = query.argument({ id }).first();
    const fragment = firstQuery.buildQueryFragment();

    return new Promise((resolve, reject) => {
      this.currentBatch.add(fragment, (data, err) => {
        if (err) return reject(err);
        const model = modelAttribute(data, { query: firstQuery });
        resolve(model);
        return model;
      });
      this.checkLimit();
    });
  }

  /**
   * Find a record by specific attributes. Returns null if the record cannot be found.
   */
  async findBy<Q extends Query<any, any> = Query<any, any>>(
    query: Q,
    filters: Record<string, any>
  ): Promise<QueryModel<Q> | null> {
    const byQuery = query.where(filters).per(1).multiple();
    const fragment = byQuery.buildQueryFragment();

    return new Promise((resolve, reject) => {
      this.currentBatch.add(fragment, (data, err) => {
        if (err) return reject(err);
        if (!data) return resolve(null);
        const model = modelAttribute(data, { query: byQuery })[0];
        resolve(model);
        return model;
      });
      this.checkLimit();
    });
  }

  /**
   * Get all models using a query
   */
  async all<Q extends Query<any, any> = Query<any, any>>(
    query: Q
  ): Promise<QueryModel<Q>[]> {
    const fragment = query.buildQueryFragment();

    return new Promise((resolve, reject) => {
      this.currentBatch.add(fragment, (data, err) => {
        if (err) return reject(err);
        const list = modelAttribute(data, { query });
        resolve(list);
        return list;
      });
      this.checkLimit();
    });
  }
}
